import { useRef, useState } from 'react';
import { useOutsideClick, useWindowResize } from '@rainbow-modules/hooks';
import { InternalOverlay } from 'react-rainbow-components';
import { PositionResolverOpts } from 'react-rainbow-components/components/InternalOverlay';
import { CircleCheck, CircleDashed, Clock } from 'lucide-react';
import { InvoiceSignatureInfo } from '../../types';
import { signatureStatusLabel } from '../../helpers';
import SignatureDetails from '../../SignatureDetails';
import { SignatureDropdown, SignatureIcon } from '../../styled';

const positionResolver = (opts: PositionResolverOpts) => {
    const { trigger, content, viewport } = opts;
    const top = trigger.leftUpAnchor.y + trigger.height + 6;
    const left = Math.max(
        14,
        Math.min(trigger.leftUpAnchor.x, viewport.width - content.width - 14),
    );
    return { top, left };
};

const SignatureStateIcon = ({ signature }: { signature: InvoiceSignatureInfo }) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const isSigned = signature.state === 'signed';

    useWindowResize(() => setIsOpen(false), isOpen);
    useOutsideClick(
        dropdownRef,
        (event) => {
            const target = event.target as Node | null;
            if (target && triggerRef.current?.contains(target)) return;
            setIsOpen(false);
        },
        isOpen,
    );

    return (
        <>
            <SignatureIcon
                ref={triggerRef}
                type="button"
                $signed={isSigned}
                aria-label={signatureStatusLabel(signature.state, signature.acceptance)}
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
            >
                {isSigned && <CircleCheck />}
                {signature.state === 'awaiting' && <Clock />}
                {signature.state === 'unrequested' && <CircleDashed />}
            </SignatureIcon>
            <InternalOverlay
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                positionResolver={positionResolver}
                isVisible={isOpen}
                triggerElementRef={() => triggerRef}
            >
                <SignatureDropdown ref={dropdownRef}>
                    <SignatureDetails
                        state={signature.state}
                        acceptance={signature.acceptance}
                        locale={signature.locale}
                    />
                </SignatureDropdown>
            </InternalOverlay>
        </>
    );
};

export default SignatureStateIcon;
